/*
 * MUSKOP swatch sampler
 *
 * Colour names don't reliably describe the garment: one supplier's "Red" is a
 * wine red, the web's "darkgray" is lighter than its "gray", and new names
 * ("Washed Blue") appear whenever a product is added. So every colour swatch
 * first shows the name-based colour from snippets/muskop-swatch-color, and
 * this script then replaces it with the colour sampled from that variant's own
 * photo - but only when the sample can be trusted:
 *
 *   - most of the sample points on the garment must agree, and differ from the
 *     photo's background;
 *   - a basic colour name ("Black", "Navy") must not sample as something far
 *     away from that colour;
 *   - if two colours of one product point at the same photo, that photo is
 *     not colour-specific, so neither sample is used.
 *
 * Anything that fails keeps its name-based colour. A merchant-uploaded swatch
 * image (Settings > Files, "<colour-handle>.png") always wins and is never
 * sampled. Groups are sampled only when they scroll near the viewport, from a
 * 120px image, and results are cached for the browsing session.
 */
(function () {
  'use strict';
  if (window.__mkSampler) return;
  window.__mkSampler = true;

  var SIZE = 120;
  var REF = {
    black: [20, 20, 20], white: [245, 245, 245], navy: [30, 35, 70], red: [180, 30, 40],
    grey: [128, 128, 128], gray: [128, 128, 128], maroon: [110, 30, 40], green: [40, 110, 60],
    pink: [235, 150, 180], blue: [40, 80, 170], tan: [200, 170, 130], orange: [230, 120, 40],
    yellow: [235, 210, 70], purple: [110, 60, 140], khaki: [190, 175, 130], brown: [110, 75, 50]
  };

  // Versioned: bump when the sampling rules change, so a shopper part-way
  // through a session does not keep results from the old logic.
  var CACHE_KEY = 'mkSwatch.v4';
  var cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch (e) {}
  function persist() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  function dist(a, b) {
    return Math.sqrt((a[0] - b[0]) * (a[0] - b[0]) + (a[1] - b[1]) * (a[1] - b[1]) + (a[2] - b[2]) * (a[2] - b[2]));
  }
  function hex(c) {
    return '#' + c.map(function (v) { return ('0' + v.toString(16)).slice(-2); }).join('');
  }
  function nameOf(el) {
    return (el.getAttribute('title') || el.getAttribute('data-c') || '').trim();
  }

  // Where each kind of swatch keeps its variant photo.
  function photoFor(el) {
    if (el.getAttribute('data-mk-photo')) return el.getAttribute('data-mk-photo');   // product page
    if (el.classList.contains('mk-sw')) {                                             // homepage grid
      var src = el.getAttribute('data-img');
      return src ? src.replace(/width=\d+/, 'width=' + SIZE) : '';
    }
    var input = el.previousElementSibling;                                            // collection cards
    if (input && input.getAttribute('data-image-url')) {
      return input.getAttribute('data-image-url').replace('{width}', String(SIZE));
    }
    return '';
  }

  function load(url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d', { willReadFrequently: true });

  // Names that describe two tones at once ("Navy Blue with Gray").
  function twoToneName(name) {
    return / with | and | & /i.test(name);
  }

  // Reads the garment. Returns {one:[r,g,b]} or {two:[left, right]} or null.
  function sample(img, wantTwo) {
    var W = SIZE, H = Math.max(1, Math.round(SIZE * img.height / img.width));
    canvas.width = W; canvas.height = H;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(img, 0, 0, W, H);
    function px(fx, fy) {
      var d = ctx.getImageData(Math.max(0, Math.round(fx * W) - 2), Math.max(0, Math.round(fy * H) - 2), 4, 4).data;
      var ch = [[], [], []];
      for (var i = 0; i < d.length; i += 4) { ch[0].push(d[i]); ch[1].push(d[i + 1]); ch[2].push(d[i + 2]); }
      return ch.map(function (v) { v.sort(function (a, b) { return a - b; }); return v[v.length >> 1]; });
    }
    var bgs = [px(0.03, 0.03), px(0.97, 0.03)];
    // Spread across the frame: some photos are flat-lays filling the image,
    // others are square shots where the garment only fills the middle band.
    var spots = [
      [0.3, 0.78], [0.7, 0.78], [0.28, 0.5], [0.72, 0.5],
      [0.5, 0.86], [0.5, 0.35], [0.35, 0.62], [0.65, 0.62]
    ];
    var pts = spots
      .map(function (s) { return { x: s[0], c: px(s[0], s[1]) }; })
      .filter(function (p) { return bgs.every(function (b) { return dist(p.c, b) > 18; }); });

    if (wantTwo) {
      // Colour-blocked garment: find the two most different points on it.
      if (pts.length < 2) return null;
      var best = null;
      for (var i = 0; i < pts.length; i++) {
        for (var j = i + 1; j < pts.length; j++) {
          var d2 = dist(pts[i].c, pts[j].c);
          if (!best || d2 > best.d) best = { d: d2, a: pts[i], b: pts[j] };
        }
      }
      if (!best || best.d <= 40) return null;          // not actually two tones
      var pair = best.a.x <= best.b.x ? [best.a.c, best.b.c] : [best.b.c, best.a.c];
      return { two: pair };
    }

    if (pts.length >= 3) {
      var m = [0, 1, 2].map(function (k) {
        var v = pts.map(function (p) { return p.c[k]; }).sort(function (a, b) { return a - b; });
        return v[v.length >> 1];
      });
      var agree = pts.filter(function (p) { return dist(p.c, m) <= 40; }).length;
      return agree >= 3 ? { one: m } : null;
    }
    // Only two points landed on the garment - common when the product fills a
    // narrow band of a square photo. Trust them if they closely agree.
    if (pts.length === 2 && dist(pts[0].c, pts[1].c) <= 30) {
      return { one: [0, 1, 2].map(function (k) { return Math.round((pts[0].c[k] + pts[1].c[k]) / 2); }) };
    }
    return null;
  }

  function sampleUrl(url, wantTwo) {
    var key = (wantTwo ? '2|' : '1|') + url;
    if (Object.prototype.hasOwnProperty.call(cache, key)) return Promise.resolve(cache[key]);
    return load(url).then(function (img) {
      var s = null;
      try { s = img ? sample(img, wantTwo) : null; } catch (e) { s = null; }
      cache[key] = s;
      persist();
      return s;
    });
  }

  function css(s) {
    return s.two
      ? 'linear-gradient(135deg,' + hex(s.two[0]) + ' 50%,' + hex(s.two[1]) + ' 50%)'
      : hex(s.one);
  }

  function paint(el, s) {
    el.style.background = css(s);
    el.setAttribute('data-mk-sampled', css(s));
    if (el.classList.contains('is-text')) {
      el.classList.remove('is-text');
      el.textContent = '';
    }
  }

  // Nothing should ever render as an empty circle, whatever a colour is called.
  var HATCH = 'repeating-linear-gradient(45deg,#d9d9d9 0 3px,#fff 3px 6px)';
  function backstop(el) {
    if (el.getAttribute('data-bg') || el.classList.contains('is-text')) return;
    var cs = window.getComputedStyle(el);
    if (cs.backgroundColor === 'rgba(0, 0, 0, 0)' && cs.backgroundImage === 'none') {
      el.style.background = HATCH;
      el.setAttribute('data-mk-checked', 'placeholder');
    }
  }

  function processGroup(group) {
    var els = Array.prototype.slice.call(group.querySelectorAll('.color-swatch__item, .mk-sw'))
      .filter(function (el) { return !el.getAttribute('data-bg') && photoFor(el); });
    if (!els.length) return Promise.resolve();

    return Promise.all(els.map(function (el) {
      return sampleUrl(photoFor(el), twoToneName(nameOf(el)));
    })).then(function (samples) {
      var ok = samples.map(function (s, i) {
        if (!s) return false;
        if (s.two) return true;                        // two-tone names skip the basic-colour check
        var key = nameOf(els[i]).toLowerCase().replace(/[^a-z]/g, '');
        return !(REF[key] && dist(s.one, REF[key]) > 150);
      });
      // Two colours pointing at the SAME photo can't both be that colour, so
      // the photo isn't colour-specific - trust neither. Comparing photos
      // rather than sampled colours matters: genuinely close colours (Army
      // Green vs Dark Gray, Black vs Dark Gray) each have their own photo and
      // must still be read from it. Photos that show a backdrop instead of the
      // garment are caught by the basic-colour check above.
      for (var i = 0; i < els.length; i++) {
        for (var j = i + 1; j < els.length; j++) {
          if (samples[i] && samples[j] &&
              nameOf(els[i]).toLowerCase() !== nameOf(els[j]).toLowerCase() &&
              photoFor(els[i]) === photoFor(els[j])) {
            ok[i] = false; ok[j] = false;
          }
        }
      }
      els.forEach(function (el, i) {
        el.setAttribute('data-mk-checked', ok[i] ? 'photo' : 'name');
        if (ok[i]) paint(el, samples[i]); else backstop(el);
      });
    });
  }

  var observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { observer.unobserve(e.target); processGroup(e.target); }
        });
      }, { rootMargin: '300px' })
    : null;

  function scan() {
    var groups = document.querySelectorAll('.color-swatch-list:not([data-mk-seen]), .mk-swatches:not([data-mk-seen])');
    Array.prototype.forEach.call(groups, function (g) {
      g.setAttribute('data-mk-seen', '');
      // Catch blanks straight away, including swatches with no photo to sample.
      Array.prototype.forEach.call(g.querySelectorAll('.color-swatch__item, .mk-sw'), backstop);
      if (observer) observer.observe(g); else processGroup(g);
    });
  }

  // Test hook: sample every group on the page now, regardless of scroll.
  window.__mkSampleAll = function () {
    scan();
    var all = document.querySelectorAll('.color-swatch-list, .mk-swatches');
    return Promise.all(Array.prototype.map.call(all, processGroup));
  };

  var queued = false;
  function queueScan() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(); });
  }

  scan();
  document.addEventListener('DOMContentLoaded', scan);
  // Quick view and "load more" insert swatches later.
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });
})();
